import { useState, useEffect } from "react";
import { Box, CircularProgress, styled } from "@mui/material";
import dayjs, { Dayjs } from 'dayjs';
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import "dayjs/locale/fr";
import { CalendarPicker } from "@mui/x-date-pickers";
import Grid from "@mui/material/Grid";

import "./weather.css";

import WeatherItem from "../../components/WeatherItem";
import WeatherItemInfos from "../../components/WeatherItemInfos";

import {
  fetchWeatherAction,
  fetchWeatherRangeAction,
} from "../address/weatherSlice";
import { useAppSelector, useAppDispatch } from "../../app/hooks";
import { selectAddress } from "../address/addressSlice";
import { selectWeatherAlert } from "../address/weatherSlice";
import { ResultBlockType } from "../../types";
import { weatherRisk } from "../../constants";
import { useTranslation } from "react-i18next";

const StyledCalendarPicker = styled(CalendarPicker<Dayjs>)((style: any) => ({
  width: "100%",
  "& .MuiPickersCalendarHeader-root": {
    paddingLeft: "12px",
  },
  "& .MuiPickersCalendarHeader-labelContainer": {
    fontSize: "1rem",
    textTransform: "capitalize",
  },
  "& .MuiPickersArrowSwitcher-spacer": {
    width: "12px",
  },
  "& .MuiPickersArrowSwitcher-button .MuiSvgIcon-root": {
    fontSize: "1.2rem",
  },
  "& .MuiDayPicker-weekContainer .MuiPickersDay-root": {
    height: "30px",
  },
  ...style,
}));

const today = new Date().toISOString();

export default function WeatherSearchResultBlock({
  title,
  id,
  iconType,
}: ResultBlockType): JSX.Element {
  const dispatch = useAppDispatch();
  const address = useAppSelector(selectAddress),
    weatherAlert = useAppSelector(selectWeatherAlert);

  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs(today));
  const [selectedAlertType, setSelectedAlertType] = useState<string | null>(
    null
  );
  const [selectedAlert, setSelectedAlert] = useState<any>(null);
  const { t } = useTranslation();

  const altitude =
    address &&
    address.value &&
    address.value.buildingData2.find(
      (data: { label: string; unit: string; value: string }) =>
        data.label === "Altitude"
    );

  const weatherRiskFilter =
    altitude?.value > 5
      ? weatherRisk.filter(
          (risk: { id: string; label: string }) => risk.label !== "Submersion"
        )
      : weatherRisk;

  useEffect(function () {
    dispatch(fetchWeatherRangeAction());
  }, [dispatch]);

  useEffect(
    function () {
      if (!address.value) {
        return;
      }
      if (address.value.inseePostalCodeData) {
        dispatch(
          fetchWeatherAction({
            date: selectedDate === null ? dayjs(today) : selectedDate.toDate(),
            postalCode: address.value.inseePostalCodeData?.properties.postal_code,
          })
        );
      }
    },
    [address, selectedDate, dispatch]
  );

  useEffect(
    function () {
      if (selectedAlertType) {
        const alert = getRiskIfExist(selectedAlertType, weatherAlert.value);
        if (alert) {
          setSelectedAlert(alert);
        }
      }
    },
    [selectedAlertType, weatherAlert.value]
  );

  useEffect(
    function () {
      setSelectedDate(dayjs(today));
      setSelectedAlert(null);
    },
    [address]
  );
  useEffect(
    function () {
      setSelectedAlert(null);
    },
    [selectedDate]
  );

  const renderWeatherItems = (): JSX.Element[] => {
    return weatherRiskFilter.map((item, index): JSX.Element => {
      let alert = getRiskIfExist(item.id, weatherAlert.value);
      return (
        <Grid
          item
          xs={3}
          sm={2}
          md={1.5}
          flexDirection="column"
          alignItems="center"
          className="WeatherItem"
          key={`weather-item-risk-${index}`}
          sx={{
            padding: "8px 0 !important",
          }}
        >
          <WeatherItem
            type={item.id}
            level={alert ? alert.level : null}
            onAlertClick={(type) => setSelectedAlertType(type)}
            label={t(item.label)}
            isDisabled={false}
          />
        </Grid>
      );
    });
  };

  const getRiskIfExist = (type: string | null, alerts: any[]): any | null => {
    if (type) {
      for (let alert of alerts) {
        if (type === alert.type) {
          return alert;
        }
      }
    }
    return null;
  };

  const renderWeatherItemsNoAlerts = (): JSX.Element[] => {
    return weatherRiskFilter.map(
      (item, index): JSX.Element => (
        <Grid
          item
          xs={3}
          sm={2}
          md={1.5}
          className="WeatherItem"
          flexDirection="column"
          alignItems="center"
          key={`weather-item-no-risk-${index}`}
          sx={{
            padding: "8px 0 !important",
          }}
        >
          <WeatherItem
            type={item.id}
            level={null}
            label={t(item.label)}
            isDisabled={address.value === undefined}
          />
        </Grid>
      )
    );
  };

  return (
    <>
      <Box sx={{ display: "flex", gap: 2 }}>
        {/* Colonne gauche fixe pour la sélection de date */}
        <Box sx={{ 
          width: "250px", 
          flexShrink: 0,
          display: "flex", 
          flexDirection: "column", 
          alignItems: "flex-start" 
        }}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={"fr"}>
            {/* <DatePicker
              value={selectedDate}
              onChange={(newValue) => {
                setSelectedDate(newValue.toISOString());
                setSelectedAlertType(null);
              }}
              renderInput={(params) => <TextField {...params} fullWidth />}
              minDate={weather.range.value?.min}
              maxDate={weather.range.value?.max}
              disabled={address.value === undefined}
            /> */}
            <StyledCalendarPicker
              onChange={(newValue: Dayjs | null) => {
                if (!newValue) {
                  return;
                }
                setSelectedDate(newValue);
              }}
              date={selectedDate}
            />
          </LocalizationProvider>
          { weatherAlert.status === 'loading' && <CircularProgress sx={{marginTop: 2}}/> }
        </Box>

        {/* Colonne droite pour le contenu */}
        <Box sx={{ flex: 1 }}>
          <Grid
            container
            spacing={1}
            alignItems="center"
            justifyContent="flex-start"
            className="WeatherItemsWrapper"
            sx={{
              minWidth: "850px",
            }}
          >
            {weatherAlert.value
              ? renderWeatherItems()
              : renderWeatherItemsNoAlerts()}
          </Grid>
          
          <Grid item xs={12} sx={{marginTop: 2}}>
            <WeatherItemInfos
              alert={selectedAlert}
              isRisk={weatherAlert.value?.length > 0 ? true : false}
              selectedAlertType={selectedAlertType}
              alertOnItemClikedIsExist={
                getRiskIfExist(selectedAlertType, weatherAlert.value)
                  ? true
                  : false
              }
              isDisabled={address.value === undefined}
            />
          </Grid>
        </Box>
      </Box>
    </>
  );
}
